// seedScorePages/SharedForms/SharedEntrepreneurInfo.js
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSharedForm } from '../../context/SharedFormContext';
import '../infoForm/EntrepreneurInfo.css';
import ProgressBar from '../../components/ProgressBar/SharedProgressBar';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { getSectorOptions, getCompanyStages, isDcfOnlyCompanyStage } from '../sectorOptions';

const SharedEntrepreneurInfo = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { sharedFormData, updateEntrepreneurInfo } = useSharedForm();
    const fileInputRef = useRef(null);
    
    const sectors = getSectorOptions(t, i18n.language);

    const stages = getCompanyStages(i18n.language);
    const [formData, setFormData] = useState({
        companyName: '',
        sector: '',
        stage: '',
        foundingDate: '',
        currency: '',
        valuationCurrency: '',
        companyLogo: null,
        email: '',
        contactName: '',
        phoneNumber: ''
    });
    const isDcfOnly = isDcfOnlyCompanyStage(formData.stage);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoError, setLogoError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        
        let savedEmail = localStorage.getItem('sharedFormEmail');
        let savedName = localStorage.getItem('sharedFormName');
        
        if (!savedEmail && sharedFormData.entrepreneurInfo?.email) {
            savedEmail = sharedFormData.entrepreneurInfo.email;
            savedName = sharedFormData.entrepreneurInfo.contactName;
            
            localStorage.setItem('sharedFormEmail', savedEmail);
            if (savedName) {
                localStorage.setItem('sharedFormName', savedName);
            }
        }
        
        if (!savedEmail) {
            const timer = setTimeout(() => {
                const retryEmail = localStorage.getItem('sharedFormEmail');
                const retryName = localStorage.getItem('sharedFormName');
                const contextEmail = sharedFormData.entrepreneurInfo?.email;
                
                if (retryEmail || contextEmail) {
                    const finalEmail = retryEmail || contextEmail;
                    const finalName = retryName || sharedFormData.entrepreneurInfo?.contactName || '';
                    
                    if (!retryEmail && contextEmail) {
                        localStorage.setItem('sharedFormEmail', contextEmail);
                        localStorage.setItem('sharedFormName', finalName);
                    }
                    
                    setFormData(prev => ({
                        ...prev,
                        email: finalEmail,
                        contactName: finalName
                    }));
                    setIsInitialized(true);
                } else {
                    navigate(`/shared-form/${token}`);
                }
            }, 500); 
            
            return () => clearTimeout(timer);
        } else {
            setFormData(prev => ({
                ...prev,
                email: savedEmail,
                contactName: savedName || ''
            }));
            setIsInitialized(true);
        }
    }, [token, navigate, sharedFormData.entrepreneurInfo]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        setLogoError('');

        if (!file) {
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setLogoError(t('sharedEntrepreneurInfo.logoError'));
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setLogoError(t('sharedEntrepreneurInfo.logoSizeError'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const base64String = event.target.result;
            setLogoPreview(base64String);
            setFormData({ ...formData, companyLogo: base64String });
        };
        reader.onerror = () => {
            setLogoError(t('sharedEntrepreneurInfo.logoReadError'));
        };
        reader.readAsDataURL(file);
    };

    const removeLogo = () => {
        setLogoPreview(null);
        setFormData({ ...formData, companyLogo: null });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const saveDraft = async () => {
        setIsSaving(true);
        try {
            let email = formData.email || localStorage.getItem('sharedFormEmail');
            
            if (!email) {
                alert(t('sharedEntrepreneurInfo.emailNotFound'));
                navigate(`/shared-form/${token}`);
                return;
            }
            
            localStorage.setItem('sharedFormEmail', email);
            
            const response = await fetch(`/api/shared-links/save-draft/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: email,
                    step: 'entrepreneur-info',
                    data: formData
                })
            });
            
            const responseData = await response.json();
            
            if (response.ok) {
                alert(t('sharedEntrepreneurInfo.saveSuccess'));
            } else {
                throw new Error(responseData.msg || 'Save failed');
            }
        } catch (error) {
            console.error('Save error:', error);
            alert(`${t('sharedEntrepreneurInfo.saveError')} ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleNext = async (e) => {
        e.preventDefault();
        
        try {
            let email = formData.email || localStorage.getItem('sharedFormEmail');
            
            if (!email) {
                alert(t('sharedEntrepreneurInfo.emailNotFound'));
                navigate(`/shared-form/${token}`);
                return;
            }
            
            localStorage.setItem('sharedFormEmail', email);
            
            await fetch(`/api/shared-links/save-draft/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: email,
                    step: 'entrepreneur-info',
                    data: formData
                })
            });
        } catch (error) {
            console.error('Auto-save error:', error);
        }
        
        updateEntrepreneurInfo(formData);
        navigate(`/shared-form/${token}/financial-table`);
    };

    const handleBack = () => {
        navigate(`/shared-form/${token}`);
    };

    if (!isInitialized) {
        return (
            <div className="shared-entrepreneur-page">
                <div className="info-form-container">
                    <p>{t('sharedEntrepreneurInfo.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="shared-entrepreneur-page">
            <div className="shared-form-language-switcher" style={{ position: 'absolute', top: '20px', right: '20px' }}>
                <LanguageSwitcher />
            </div>
            
            <div className="info-form-container">
                {!isDcfOnly && <div className="progress-section">
                    <ProgressBar currentStep={1} />
                </div>}
                <div className="progress-section">
                    <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: '33%' }}></div>
                    </div>
                </div>
                
                <h2>{t('sharedEntrepreneurInfo.title')}</h2>
                <p>{t('sharedEntrepreneurInfo.subtitle')}</p>
                
                <form onSubmit={handleNext} className="info-form">
                    <div className="top-form-section">
                        <div className="company-info-section">
                            <div className="form-group">
                                <label htmlFor="companyName">{t('sharedEntrepreneurInfo.companyName')}</label>
                                <input
                                    type="text"
                                    id="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    placeholder={t('sharedEntrepreneurInfo.companyNamePlaceholder')}
                                    required
                                />
                            </div>

                            <div className="form-group">
                        <label htmlFor="contactName">{t('sharedEntrepreneurInfo.contactName')}</label>
                        <input
                            type="text"
                            id="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phoneNumber">{t('sharedEntrepreneurInfo.phoneNumber')}</label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            placeholder="+90 5XX XXX XX XX"
                            required
                        />
                    </div>

                            <div className="form-group">
                                <label htmlFor="sector">{t('sharedEntrepreneurInfo.sector')}</label>
                                <select id="sector" value={formData.sector} onChange={handleChange} required>
                                    <option value="" disabled>{t('sharedEntrepreneurInfo.selectSector')}</option>
                                    {sectors.map((sector, index) => (
                                        <option key={index} value={sector.key}>{sector.value}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="stage">{i18n.language?.startsWith('en') ? 'Company Stage' : 'Şirket Aşaması'}</label>
                                <select id="stage" value={formData.stage} onChange={handleChange} required>
                                    <option value="" disabled>{i18n.language?.startsWith('en') ? 'Select company stage' : 'Şirket aşamasını seçin'}</option>
                                    {stages.map((stage, index) => (
                                        <option key={stage.key} value={stage.value}>{stage.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="logo-section">
                            <div className="form-group">
                                <label>{t('sharedEntrepreneurInfo.companyLogo')}</label>
                                <div className="logo-upload-container">
                                    <div className="logo-preview-area">
                                        {logoPreview ? (
                                            <div className="logo-preview">
                                                <img src={logoPreview} alt="Company logo" />
                                                <button
                                                    type="button"
                                                    className="remove-logo-btn"
                                                    onClick={removeLogo}
                                                    title="Remove logo"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="logo-placeholder" onClick={triggerFileInput}>
                                                <div className="upload-icon">📷</div>
                                                <p>{t('sharedEntrepreneurInfo.uploadLogo')}</p>
                                                <span className="upload-hint">{t('sharedEntrepreneurInfo.logoHint')}</span>
                                            </div>
                                        )}
                                    </div>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleLogoUpload}
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                    />

                                    {logoError && (
                                        <div className="logo-error">{logoError}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="foundingDate">{t('sharedEntrepreneurInfo.foundingDate')}</label>
                            <input 
                                type="date" 
                                id="foundingDate" 
                                value={formData.foundingDate} 
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="currency">{t('sharedEntrepreneurInfo.financialCurrency')}</label>
                            <select id="currency" value={formData.currency} onChange={handleChange} required>
                                <option value="" disabled>{t('sharedEntrepreneurInfo.selectCurrency')}</option>
                                <option value="₺">₺ (TRY)</option>
                                <option value="€">€ (EUR)</option>
                                <option value="$">$ (USD)</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="valuationCurrency">{t('sharedEntrepreneurInfo.valuationCurrency')}</label>
                        <select id="valuationCurrency" value={formData.valuationCurrency} onChange={handleChange} required>
                            <option value="" disabled>{t('sharedEntrepreneurInfo.selectCurrency')}</option>
                            <option value="₺">₺ (TRY)</option>
                            <option value="€">€ (EUR)</option>
                            <option value="$">$ (USD)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{t('sharedEntrepreneurInfo.contactEmail')}</label>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            readOnly
                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div className="navigation-buttons">
                        <button
                            type="button"
                            className="save-draft-btn"
                            onClick={saveDraft}
                            disabled={isSaving}
                        >
                            {isSaving ? t('sharedEntrepreneurInfo.saving') : t('sharedEntrepreneurInfo.save')}
                        </button>

                        <div className="nav-buttons-group">
                            <button type="button" className="nav-button back-button" onClick={handleBack}>
                                {t('sharedEntrepreneurInfo.previous')}
                            </button>
                            <button type="submit" className="nav-button next-button">
                                {t('sharedEntrepreneurInfo.nextStep')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SharedEntrepreneurInfo;
